import type { CampanaEnvioPublic } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Edit2, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { emisionApi } from '../api/emision.api';
import { Resultados, SelloEstado } from '../components/Piezas';

// ciclo-emisor, parte 2: la lista de campañas de envío. Cada una con su avance, sus
// resultados a la vista y la casilla Activa (la decisión es de Javier, a mano).

export function CampanasPage(): JSX.Element {
  const es = useI18n();
  const t = es.campanasEnvio;
  const { data, isLoading, error } = useQuery({ queryKey: qk.campanasEnvio.all, queryFn: emisionApi.list });

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-2 flex items-center justify-between">
        <h1 className="font-titulos text-3xl font-bold text-text-marca">{t.titulo}</h1>
        <Button asChild>
          <Link to="/campanas/nueva">
            <Plus size={16} />
            {t.nueva}
          </Link>
        </Button>
      </div>
      <p className="mb-6 max-w-3xl text-sm text-text-subtle">{t.intro}</p>

      {error && <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>}
      {isLoading && <p className="text-sm text-text-subtle">{t.cargando}</p>}
      {data && data.length === 0 && <p className="text-sm text-text-faint">{t.vacio}</p>}

      <div className="space-y-3">
        {data?.map((c) => (
          <TarjetaCampana key={c.id} c={c} />
        ))}
      </div>
    </div>
  );
}

function TarjetaCampana({ c }: { c: CampanaEnvioPublic }): JSX.Element {
  const es = useI18n();
  const t = es.campanasEnvio;
  const queryClient = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  const activar = useMutation({
    mutationFn: (activa: boolean) => emisionApi.actualizar(c.id, { activa }),
    onSuccess: (r) => {
      toast.success(r.activa ? t.activada : t.pausada);
      setConfirmar(false);
      void queryClient.invalidateQueries({ queryKey: qk.campanasEnvio.all });
    },
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });
  const alTildar = (): void => {
    if (c.activa) activar.mutate(false);
    // La primera vez fija la lista: se confirma. Reanudar una pausada, no.
    else if (c.estado === 'BORRADOR') setConfirmar(true);
    else activar.mutate(true);
  };
  const r = c.resultados;

  return (
    <div className="flex items-center gap-6 rounded-xl border border-border-subtle bg-surface p-4 shadow-card">
      <label className="flex shrink-0 flex-col items-center gap-1 text-xs text-text-subtle">
        <input
          type="checkbox"
          className="h-5 w-5 accent-marca-primario"
          checked={c.activa}
          disabled={c.estado === 'TERMINADA' || activar.isPending}
          onChange={alTildar}
          aria-label={`${t.activa} ${c.nombre}`}
        />
        {t.activa}
      </label>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <Link
            to={`/campanas/${c.id}`}
            className="truncate font-titulos text-lg font-bold text-text-strong hover:underline"
          >
            {c.nombre}
          </Link>
          <SelloEstado estado={c.estado} />
          <span className="text-sm text-text-subtle">
            {c.listaFijadaEn
              ? t.progreso.replace('{enviados}', String(r.enviados)).replace('{total}', String(r.total))
              : t.sinLista}
          </span>
        </div>
        {c.listaFijadaEn && <Resultados r={r} compacto />}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/campanas/${c.id}`}>
            <BarChart3 size={14} />
            {t.verDetalle}
          </Link>
        </Button>
        {c.estado !== 'TERMINADA' && (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/campanas/${c.id}/editar`} aria-label={`${t.editar} ${c.nombre}`}>
              <Edit2 size={14} />
            </Link>
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirmar}
        onOpenChange={setConfirmar}
        titulo={t.confirmarActivar.titulo.replace('{nombre}', c.nombre)}
        descripcion={t.confirmarActivar.cuerpo}
        confirmar={t.confirmarActivar.confirmar}
        cancelar={t.confirmarActivar.cancelar}
        pendiente={activar.isPending}
        onConfirmar={() => activar.mutate(true)}
      />
    </div>
  );
}
